import React, { useState, useEffect } from 'react'
import { X, Sparkles, Loader2, AlertTriangle } from 'lucide-react'
import Button from '@/components/ui/Button'
import Checkbox from '@/components/ui/Checkbox'
import BusinessSelect from '@/components/ui/BusinessSelect'
import DepartmentSelect from '@/components/ui/DepartmentSelect'
import ReportTemplateSelect from '@/components/ui/ReportTemplateSelect'
import { designTokens } from '@/utils/design-tokens'
import { useAppSelector } from '@/store/hooks'
import { useAlert } from '@/components/ui/Alert/exports'
import { JobsService } from '@/api/generated/services/JobsService'
import { TaskReportService } from '@/api/generated/services/TaskReportService'
import type { ReportTemplateDetailResponse } from '@/api/generated/models/ReportTemplateDetailResponse'
import { handleApiError, ErrorMessages } from '@/utils/api-error-handler'

type BatchScope = 'workspace' | 'business' | 'department'

interface BatchAiAnalysisModalProps {
  isOpen: boolean
  onClose: () => void
  workspaceId: string
  /** 페이지 필터의 현재 선택값 — 모달 열릴 때 기본값으로 반영 */
  defaultBusinessId?: string
  defaultBusinessName?: string
  defaultDepartmentId?: string
  defaultDepartmentName?: string
  defaultReportTemplateKey?: string
  defaultReportTemplateName?: string
  /** 작업 등록 성공 시 호출 (배너 갱신 트리거) */
  onStarted?: (jobId: string) => void
}

const SCOPE_OPTIONS: Array<{ value: BatchScope; label: string }> = [
  { value: 'workspace', label: '워크스페이스 전체' },
  { value: 'business', label: '사업별' },
  { value: 'department', label: '부서별' },
]

/**
 * 일괄 AI 분석 실행 모달.
 * 범위(워크스페이스/사업/부서)와 보고서 유형(미지정 = 사업별 유효 템플릿 전체),
 * 기존 분석값 덮어쓰기 여부를 설정한 뒤 분석 작업(Job)을 등록한다.
 */
const BatchAiAnalysisModal: React.FC<BatchAiAnalysisModalProps> = ({
  isOpen,
  onClose,
  workspaceId,
  defaultBusinessId = '',
  defaultBusinessName = '',
  defaultDepartmentId = '',
  defaultDepartmentName = '',
  defaultReportTemplateKey = '',
  defaultReportTemplateName = '',
  onStarted,
}) => {
  const { showSuccess, showInfo, showError } = useAlert()
  const { businesses, isLoading: businessLoading, error: businessError } = useAppSelector(
    state => state.business
  )

  const [scope, setScope] = useState<BatchScope>('workspace')
  const [businessId, setBusinessId] = useState('')
  const [businessName, setBusinessName] = useState('')
  const [departmentId, setDepartmentId] = useState('')
  const [departmentName, setDepartmentName] = useState('')
  const [templateKey, setTemplateKey] = useState('')
  const [templateName, setTemplateName] = useState('')
  const [overwrite, setOverwrite] = useState(false)
  // 완료된 수행업무만 분석 (기본 켬)
  const [completedOnly, setCompletedOnly] = useState(true)

  const [templates, setTemplates] = useState<ReportTemplateDetailResponse[]>([])
  const [isTemplatesLoading, setIsTemplatesLoading] = useState(false)
  const [templatesError, setTemplatesError] = useState<string | null>(null)

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // 모달이 열릴 때 페이지 필터값을 기본값으로 반영 (부서 > 사업 > 전체 순으로 좁은 범위 우선)
  useEffect(() => {
    if (!isOpen) return

    if (defaultDepartmentId) {
      setScope('department')
    } else if (defaultBusinessId) {
      setScope('business')
    } else {
      setScope('workspace')
    }
    setBusinessId(defaultBusinessId)
    setBusinessName(defaultBusinessName)
    setDepartmentId(defaultDepartmentId)
    setDepartmentName(defaultDepartmentName)
    setTemplateKey(defaultReportTemplateKey)
    setTemplateName(defaultReportTemplateName)
    setOverwrite(false)
    setError(null)
  }, [
    isOpen,
    defaultBusinessId,
    defaultBusinessName,
    defaultDepartmentId,
    defaultDepartmentName,
    defaultReportTemplateKey,
    defaultReportTemplateName,
  ])

  // 보고서 유형 목록 — 사업 범위면 해당 사업의 유효 템플릿만, 그 외에는 워크스페이스 전체
  useEffect(() => {
    if (!isOpen || !workspaceId) return

    let cancelled = false
    setIsTemplatesLoading(true)
    setTemplatesError(null)

    TaskReportService.appsTaskViewsGetReportTemplatesByWorkspace({
      workspaceId,
      businessId: scope === 'business' && businessId ? businessId : undefined,
    })
      .then(res => {
        if (cancelled) return
        setTemplates(res ?? [])
      })
      .catch(err => {
        if (cancelled) return
        console.error('Failed to load report templates:', err)
        setTemplatesError('보고서 유형 목록을 불러오는데 실패했습니다.')
      })
      .finally(() => {
        if (!cancelled) setIsTemplatesLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [isOpen, workspaceId, scope, businessId])

  if (!isOpen) return null

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !isSubmitting) {
      onClose()
    }
  }

  const handleSubmit = async () => {
    if (scope === 'business' && !businessId) {
      setError('분석할 사업을 선택해주세요.')
      return
    }
    if (scope === 'department' && !departmentId) {
      setError('분석할 부서를 선택해주세요.')
      return
    }

    setIsSubmitting(true)
    setError(null)

    try {
      const targetId =
        scope === 'business' ? businessId : scope === 'department' ? departmentId : null

      const res = await JobsService.appsJobsViewsCreateAiAnalysisJob({
        requestBody: {
          scope,
          workspace_id: workspaceId,
          target_id: targetId,
          report_template_key: templateKey || null,
          overwrite,
          completed_only: completedOnly,
        },
      })

      if (res.existing) {
        showInfo('동일한 분석 작업이 이미 진행 중입니다. 상단 배너에서 진행 상황을 확인하세요.')
      } else if (res.total_items === 0) {
        showInfo(
          res.skipped > 0
            ? `등록된 분석 대상이 없습니다. ${res.skipped}건은 이미 진행 중이거나 기존 분석값이 있어 건너뛰었습니다.`
            : '조건에 해당하는 분석 대상이 없습니다.'
        )
      } else {
        showSuccess(
          `AI 분석 ${res.total_items}건이 등록되었습니다.` +
            (res.skipped > 0
              ? ` (${res.skipped}건은 이미 진행 중이거나 기존 분석값이 있어 건너뜀)`
              : '')
        )
      }

      onStarted?.(res.job_id)
      onClose()
    } catch (err) {
      console.error('Failed to create batch AI analysis job:', err)
      handleApiError(err, showError, ErrorMessages.CREATE_FAILED)
    } finally {
      setIsSubmitting(false)
    }
  }

  const labelStyle: React.CSSProperties = {
    fontFamily: designTokens.typography.fontFamily.sans[0],
    fontSize: '14px',
    fontWeight: 600,
    color: designTokens.colors.gray[6],
  }

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
      data-modal-overlay
      onMouseDown={e => e.stopPropagation()}
      onClick={handleBackdropClick}
    >
      <div
        className="bg-white rounded"
        style={{
          width: '480px',
          padding: '20px',
          border: `1px solid ${designTokens.colors.gray[1]}`,
          borderRadius: '4px',
        }}
      >
        {/* Header */}
        <div
          className="flex justify-between items-center"
          style={{
            padding: '7px 0',
            borderBottom: `1px solid ${designTokens.colors.gray[1]}`,
            marginBottom: '16px',
          }}
        >
          <h3
            className="flex items-center gap-2"
            style={{
              fontFamily: designTokens.typography.fontFamily.sans[0],
              fontSize: '18px',
              fontWeight: 700,
              color: designTokens.colors.gray[6],
            }}
          >
            <Sparkles className="w-5 h-5" strokeWidth={2} />
            일괄 AI 분석
          </h3>
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="p-1 rounded hover:bg-gray-100 transition-colors"
            aria-label="닫기"
          >
            <X className="w-5 h-5" style={{ color: designTokens.colors.gray[4] }} />
          </button>
        </div>

        <div className="space-y-5">
          {/* 분석 범위 */}
          <div className="space-y-2">
            <span style={labelStyle}>분석 범위</span>
            <div className="flex gap-2">
              {SCOPE_OPTIONS.map(option => (
                <Button
                  key={option.value}
                  size="small"
                  color={scope === option.value ? 'dark' : 'light'}
                  onClick={() => setScope(option.value)}
                  disabled={isSubmitting}
                >
                  {option.label}
                </Button>
              ))}
            </div>
          </div>

          {/* 대상 선택 — 사업/부서 범위일 때만 */}
          {scope === 'business' && (
            <div className="space-y-2">
              <span style={labelStyle}>대상 사업</span>
              <BusinessSelect
                workspaceId={workspaceId}
                businesses={businesses}
                isLoading={businessLoading}
                businessesError={businessError}
                selectedBusinessId={businessId}
                selectedBusinessName={businessName || '사업 선택'}
                onSelect={(id: string, name: string) => {
                  setBusinessId(id)
                  setBusinessName(name)
                }}
                placeholder="사업 선택"
                showAllOption={false}
              />
            </div>
          )}
          {scope === 'department' && (
            <div className="space-y-2">
              <span style={labelStyle}>대상 부서</span>
              <DepartmentSelect
                workspaceId={workspaceId}
                selectedDepartmentId={departmentId}
                selectedDepartmentName={departmentName || '부서 선택'}
                onSelect={(id: string, name: string) => {
                  setDepartmentId(id)
                  setDepartmentName(name)
                }}
                placeholder="부서 선택"
                showAllOption={false}
              />
            </div>
          )}

          {/* 보고서 유형 */}
          <div className="space-y-2">
            <span style={labelStyle}>보고서 유형</span>
            <ReportTemplateSelect
              workspaceId={workspaceId}
              templates={templates}
              isLoading={isTemplatesLoading}
              templatesError={templatesError}
              selectedReportTemplateId={templateKey}
              selectedReportTemplateName={templateName || '[전체] 보고서 유형'}
              onSelect={(key: string, name: string) => {
                setTemplateKey(key)
                setTemplateName(name)
              }}
              placeholder="[전체] 보고서 유형"
              showAllOption={true}
            />
            <p
              style={{
                fontFamily: designTokens.typography.fontFamily.sans[0],
                fontSize: '12px',
                color: designTokens.colors.gray[4],
              }}
            >
              유형을 지정하지 않으면 사업별로 유효한 모든 보고서 유형을 분석합니다.
            </p>
          </div>

          {/* 덮어쓰기 */}
          <Checkbox
            checked={overwrite}
            onChange={setOverwrite}
            disabled={isSubmitting}
            size="small"
            label="기존 AI 분석값이 있는 필드도 다시 분석 (덮어쓰기)"
          />

          {/* 분석 대상 범위 */}
          <Checkbox
            checked={completedOnly}
            onChange={setCompletedOnly}
            disabled={isSubmitting}
            size="small"
            label="완료된 수행업무만 분석"
          />

          {/* AI 분석 주의 문구 — ReportWritePanel과 동일 */}
          <div
            className="flex items-start gap-2.5 rounded p-3"
            style={{
              backgroundColor: designTokens.colors.error.light,
              border: `1px solid ${designTokens.colors.error.DEFAULT}`,
            }}
          >
            <AlertTriangle
              className="w-5 h-5 flex-shrink-0 mt-0.5"
              strokeWidth={2}
              style={{ color: designTokens.colors.error.DEFAULT }}
            />
            <div className="flex flex-col gap-1">
              <span
                style={{
                  fontFamily: designTokens.typography.fontFamily.sans[0],
                  fontSize: '14px',
                  fontWeight: 600,
                  color: designTokens.colors.gray[6],
                }}
              >
                AI 분석은 오차가 발생할 수 있으므로 참고용 초안으로만 활용해 주시기 바랍니다.
              </span>
              <span
                style={{
                  fontFamily: designTokens.typography.fontFamily.sans[0],
                  fontSize: '14px',
                  fontWeight: 700,
                  color: designTokens.colors.error.DEFAULT,
                }}
              >
                사용 전 반드시 직접 내용을 검토 및 확정해 주세요.
              </span>
            </div>
          </div>

          {/* 안내 */}
          <div
            className="rounded p-3"
            style={{
              backgroundColor: designTokens.colors.gray[0],
              border: `1px solid ${designTokens.colors.gray[1]}`,
            }}
          >
            <p
              style={{
                fontFamily: designTokens.typography.fontFamily.sans[0],
                fontSize: '12px',
                color: designTokens.colors.gray[5],
                lineHeight: 1.5,
              }}
            >
              대상 건수가 많으면 완료까지 수 시간이 걸릴 수 있습니다. 분석은 서버에서
              진행되며, 진행 상황은 보고서 관리 화면 상단 배너에서 확인하고 취소할 수
              있습니다.
            </p>
          </div>

          {/* Error */}
          {error && (
            <p
              style={{
                fontFamily: designTokens.typography.fontFamily.sans[0],
                fontSize: '13px',
                color: designTokens.colors.error.DEFAULT,
              }}
            >
              {error}
            </p>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-1">
            <Button size="small" color="white" onClick={onClose} disabled={isSubmitting}>
              취소
            </Button>
            <Button
              size="small"
              color="dark"
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="flex items-center gap-1.5"
            >
              {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2} />}
              {isSubmitting ? '등록 중...' : '분석 시작'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default BatchAiAnalysisModal
